import { z } from 'zod';
import { placeholderFortuneImage } from '@/appconstants';

const Author = z.object({
  image: z.string().nullish(),
  name: z.string().catch('Fortune Editors'),
  newsletterImage: z.string().catch(''),
  url: z.preprocess(
    (value) => (value === null || value === undefined ? '' : value),
    z.string().catch('/author/fortune-editors'),
  ),
  withoutLinks: z
    .preprocess((value) => (value === null ? false : value), z.boolean().optional().default(false))
    .catch(false),
});

export const ArticlePreview = z.object({
  authors: z.array(Author).catch([
    {
      image: '',
      name: 'Fortune Editors',
      newsletterImage: '',
      url: '/author/fortune-editors',
      withoutLinks: false,
    },
  ]),
  classification: z
    .object({
      primaryPostTag: z.object({
        databaseId: z.number().catch(-1),
        name: z.string().catch(''),
        url: z.string().catch('/'),
      }),
    })
    .catch({
      primaryPostTag: {
        databaseId: -1,
        name: '',
        url: '/',
      },
    }),
  databaseId: z.number().catch(-1),
  dateGmt: z.string().transform((rawString) => {
    if (!Number(Date.parse(rawString))) {
      return new Date().toISOString();
    }
    // Necessary as some values are local when they should be in UTC
    if (!rawString.endsWith('Z')) {
      return `${rawString}Z`;
    }
    return rawString;
  }),
  excerpt: z.string().catch('placeholder excerpt'),
  // NOTE: Need to figure out on the backend how to ensure all articles have an id
  id: z.string().catch('placeholder id'),
  image: z.string().catch(placeholderFortuneImage),
  // NOTE: Should probably update the backend to require an image alt
  imageAlt: z.string().catch('placeholder alt text'),
  newsletters: z.object({
    nodes: z.array(
      z.object({
        name: z.string(),
        slug: z.string(),
      }),
    ),
  }),
  primarySectionId: z
    .string()
    .transform((rawString) => {
      const cleanedString = rawString.replace(/^"|"$/g, '');
      const num = Number(cleanedString);

      return Number.isNaN(num) ? -1 : num;
    })
    .nullable(),
  sections: z
    .object({
      nodes: z.array(
        z.object({
          databaseId: z.number(),
          name: z.string(),
          url: z.string(),
        }),
      ),
    })
    .nullable(),
  slug: z.string().catch('/'),
  title: z.string().catch('Title'),
  url: z.string().catch('/'),
});

export const ArticlePreviewHero = ArticlePreview.pick({
  authors: true,
  databaseId: true,
  dateGmt: true,
  excerpt: true,
  image: true,
  imageAlt: true,
  primarySectionId: true,
  sections: true,
  slug: true,
  title: true,
  url: true,
});

export const ArticlePreviewRailFull = ArticlePreview.pick({
  authors: true,
  databaseId: true,
  dateGmt: true,
  image: true,
  imageAlt: true,
  primarySectionId: true,
  sections: true,
  slug: true,
  title: true,
  url: true,
});

export const ArticlePreviewRailSimple = ArticlePreview.pick({
  databaseId: true,
  dateGmt: true,
  slug: true,
  title: true,
  url: true,
});

export const ArticlePreviewFeatured = ArticlePreview.pick({
  authors: true,
  databaseId: true,
  dateGmt: true,
  excerpt: true,
  image: true,
  imageAlt: true,
  slug: true,
  title: true,
  url: true,
});

export const ArticlePreviewSmall = ArticlePreview.pick({
  authors: true,
  databaseId: true,
  dateGmt: true,
  image: true,
  imageAlt: true,
  slug: true,
  title: true,
  url: true,
});

export const ArticlePreviewMostPopular = ArticlePreview.pick({
  authors: true,
  dateGmt: true,
  image: true,
  imageAlt: true,
  title: true,
  url: true,
}).extend({
  sectionName: z.string('Eyebrow'),
  sectionUrl: z.string('/'),
});

export const ArticlePreviewGrid = ArticlePreview.pick({
  authors: true,
  databaseId: true,
  dateGmt: true,
  image: true,
  imageAlt: true,
  primarySectionId: true,
  sections: true,
  slug: true,
  title: true,
  url: true,
});

export const ArticlePreviewNewsletter = ArticlePreview.pick({
  authors: true,
  classification: true,
  databaseId: true,
  primarySectionId: true,
  sections: true,
  slug: true,
  title: true,
  url: true,
});

export type ArticlePreview = z.infer<typeof ArticlePreview>;
export type ArticlePreviewRailFull = z.infer<typeof ArticlePreviewRailFull>;
export type ArticlePreviewRailSimple = z.infer<typeof ArticlePreviewRailSimple>;
export type ArticlePreviewHero = z.infer<typeof ArticlePreviewHero>;
export type ArticlePreviewSmall = z.infer<typeof ArticlePreviewSmall>;
export type ArticlePreviewFeatured = z.infer<typeof ArticlePreviewFeatured>;
export type ArticlePreviewMostPopular = z.infer<typeof ArticlePreviewMostPopular>;
export type ArticlePreviewGrid = z.infer<typeof ArticlePreviewGrid>;
export type ArticlePreviewNewsletter = z.infer<typeof ArticlePreviewNewsletter>;
